import React from 'react';
import { MapPin, SlidersHorizontal } from 'lucide-react';

/**
 * FiltersSidebar - Left sidebar with all vehicle search filters
 * Mimics Cars.com filter structure but focused on test drive booking
 */
const FiltersSidebar = ({ filters, onFilterChange, onApplyFilters }) => {
  const popularMakes = [
    'Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW',
    'Mercedes-Benz', 'Tesla', 'Nissan', 'Audi', 'Lexus'
  ];

  const bodyTypes = ['SUV', 'Sedan', 'Truck', 'EV'];
  const availabilityOptions = [
    { value: 'today', label: 'Available Today' },
    { value: 'week', label: 'Available This Week' },
    { value: 'all', label: 'Any Availability' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    onFilterChange({
      ...filters,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-5 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2 pb-4 border-b border-app-border-light dark:border-app-border">
        <SlidersHorizontal className="w-5 h-5 text-app-accent" />
        <h2 className="text-lg font-bold text-app-text-light dark:text-app-text">
          Filters
        </h2>
      </div>

      {/* BASICS SECTION */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-app-text-light dark:text-app-text uppercase tracking-wide">
          Basics
        </h3>

        {/* Make */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Make
          </label>
          <select
            name="make"
            value={filters.make}
            onChange={handleChange}
            className="w-full px-3 py-2.5 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent text-sm"
          >
            <option value="">Any Make</option>
            {popularMakes.map(make => (
              <option key={make} value={make}>{make}</option>
            ))}
          </select>
        </div>

        {/* Model */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Model
          </label>
          <input
            type="text"
            name="model"
            value={filters.model}
            onChange={handleChange}
            placeholder="e.g., Camry, Accord"
            className="w-full px-3 py-2.5 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary text-sm"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Body Type
          </label>
          <select
            name="type"
            value={filters.type}
            onChange={handleChange}
            className="w-full px-3 py-2.5 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent text-sm"
          >
            <option value="">Any Type</option>
            {bodyTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Location
          </label>
          <div className="relative">
            <input
              type="text"
              name="location"
              value={filters.location}
              onChange={handleChange}
              placeholder="City, State, or ZIP"
              className="w-full px-3 py-2.5 pl-9 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary text-sm"
            />
            <MapPin className="w-4 h-4 text-app-text-secondary-light dark:text-app-text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        {/* Radius */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Search Radius: {filters.radius} miles
          </label>
          <input
            type="range"
            name="radius"
            min="10"
            max="100"
            step="10"
            value={filters.radius}
            onChange={handleChange}
            className="w-full h-2 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary rounded-lg appearance-none cursor-pointer accent-app-accent"
          />
          <div className="flex justify-between text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
            <span>10 mi</span>
            <span>100 mi</span>
          </div>
        </div>

        {/* Mileage */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Max Mileage
          </label>
          <select
            name="maxMileage"
            value={filters.maxMileage}
            onChange={handleChange}
            className="w-full px-3 py-2.5 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent text-sm"
          >
            <option value="">Any Mileage</option>
            <option value="25000">Under 25,000</option>
            <option value="40000">Under 40,000</option>
            <option value="60000">Under 60,000</option>
            <option value="80000">Under 80,000</option>
            <option value="100000">Under 100,000</option>
          </select>
        </div>

        {/* Availability */}
        <div>
          <label className="block text-sm font-medium text-app-text-light dark:text-app-text mb-2">
            Availability
          </label>
          <div className="space-y-2">
            {availabilityOptions.map(option => (
              <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="availability"
                  value={option.value}
                  checked={filters.availability === option.value}
                  onChange={handleChange}
                  className="w-4 h-4 text-app-accent border-app-border-light dark:border-app-border focus:ring-app-accent"
                />
                <span className="text-sm text-app-text-light dark:text-app-text">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* DEALERSHIP OPTIONS SECTION */}
      <div className="space-y-4 pt-4 border-t border-app-border-light dark:border-app-border">
        <h3 className="text-sm font-semibold text-app-text-light dark:text-app-text uppercase tracking-wide">
          Dealership Options
        </h3>

        {/* Verified Only */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="verifiedOnly"
            checked={filters.verifiedOnly}
            onChange={handleChange}
            className="w-4 h-4 text-app-accent border-app-border-light dark:border-app-border rounded focus:ring-app-accent"
          />
          <span className="text-sm text-app-text-light dark:text-app-text">
            Verified dealerships only
          </span>
        </label>

        {/* Show Shippable */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="includeShippable"
            checked={filters.includeShippable}
            onChange={handleChange}
            className="w-4 h-4 text-app-accent border-app-border-light dark:border-app-border rounded focus:ring-app-accent"
          />
          <span className="text-sm text-app-text-light dark:text-app-text">
            Show shippable / multi-location dealers
          </span>
        </label>
      </div>

      {/* Apply Filters Button */}
      <button
        type="button"
        onClick={onApplyFilters}
        className="w-full bg-app-accent hover:bg-app-accent/90 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-lg"
      >
        Apply Filters
      </button>
    </div>
  );
};

export default FiltersSidebar;
